import { useId, type ReactNode } from 'react'
import { Modal } from '@/React/Shared/Component/Modal'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'
import { Button } from '@/React/Shared/Component/Button'

export type ModalConfirmationColor = 'danger' | 'info' | 'warning' | 'success'

export interface ModalConfirmationProps {
    show: boolean
    title: ReactNode
    color?: ModalConfirmationColor
    closeActionText?: string
    action?: (() => void) | null
    actionText?: string
    actionIconName?: IconNameEnum
    actionProcessing?: boolean
    onClose?: () => void
    showCloseButton?: boolean
    children?: ReactNode
}

interface ColorStyleConfig {
    bgColor: string
    iconClass: string
    iconName: IconNameEnum
}

const colorConfigs: Record<ModalConfirmationColor, ColorStyleConfig> = {
    danger: {
        bgColor: 'bg-red-100',
        iconClass: 'w-8 h-8 text-red-600',
        iconName: IconNameEnum.Alert,
    },
    info: {
        bgColor: 'bg-blue-100',
        iconClass: 'w-8 h-8 text-blue-600',
        iconName: IconNameEnum.InfoCircle,
    },
    success: {
        bgColor: 'bg-green-100',
        iconClass: 'w-8 h-8 text-green-600',
        iconName: IconNameEnum.CircleCheck,
    },
    warning: {
        bgColor: 'bg-yellow-100',
        iconClass: 'w-8 h-8 text-yellow-500',
        iconName: IconNameEnum.CloseCircle,
    },
}

export function ModalConfirmation({
    show,
    title,
    color = 'danger',
    closeActionText = 'Cancel',
    action = null,
    actionText = 'Yes',
    actionIconName,
    actionProcessing = false,
    onClose,
    showCloseButton = true,
    children,
}: ModalConfirmationProps) {
    const titleId = useId()
    const config = colorConfigs[color] ?? colorConfigs.danger

    return (
        <Modal.Container
            show={show}
            onClose={onClose}
            aria-labelledby={titleId}
        >
            <Modal.Header showCloseButton={showCloseButton} />
            <Modal.Content>
                <div className="flex flex-col items-center gap-4">
                    <div className={`p-3 rounded-full ${config.bgColor}`}>
                        <Icon
                            name={config.iconName}
                            className={config.iconClass}
                            aria-hidden="true"
                        />
                    </div>
                    <div className="space-y-2 py-4 text-center">
                        <div
                            id={titleId}
                            className="text-gray-800 font-bold text-lg"
                        >
                            {title}
                        </div>
                        {children ? (
                            <div className="text-gray-600 text-sm">
                                {children}
                            </div>
                        ) : null}
                    </div>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    onClick={onClose}
                    color="light"
                >
                    {closeActionText}
                </Button>
                {action ? (
                    <Button
                        onClick={action}
                        color={color}
                        iconName={actionIconName}
                        loading={actionProcessing}
                    >
                        {actionText}
                    </Button>
                ) : null}
            </Modal.Actions>
        </Modal.Container>
    )
}
